import React from 'react';
import { styled, css } from '@compiled/react';
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { ISSUE_TABLE_ROW_INNER_HEIGHT } from '../../../common/constants';
import type { Trend } from '../../../common/types';
import { TrendIcon } from '../../../common/ui/trend-icon';
import { comparePeriodStatistics } from '../../../common/utils/compare-period';
import { useFormatDurationFromSeconds } from '../../../common/utils/date-range';
import messages from './messages';

type Props = {
	issueCycleTime: number;
	cycleTimeStatistic: number | null;
};

const getIconTooltipTranslatedMessage = (trend: Trend, formatMessage: FormatMessage): string => {
	switch (trend) {
		case 'under':
			return formatMessage(messages.underTwelveWeekMedian);
		case 'over':
			return formatMessage(messages.overTwelveWeekMedian);
		case 'equal':
			return '';
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(trend as never);
			throw new Error('Unknown trend is given to getIconTooltipTranslatedMessage');
	}
};

export const CycleTimeCellContent = ({ cycleTimeStatistic, issueCycleTime }: Props) => {
	const { formatMessage } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	const duration = formatDurationFromSeconds(issueCycleTime, {
		days: 'short',
		hours: 'short',
	});
	const trend =
		cycleTimeStatistic === null
			? null
			: comparePeriodStatistics(issueCycleTime, cycleTimeStatistic);

	return (
		<Wrapper>
			{trend !== null && (
				<Tooltip content={getIconTooltipTranslatedMessage(trend, formatMessage)} position="right">
					<TrendIcon trend={trend} />
				</Tooltip>
			)}
			<Hours trend={trend}>{duration}</Hours>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ISSUE_TABLE_ROW_INNER_HEIGHT}px`,
});

const truncateTextStyles = css({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Hours = styled.div<{ trend: any }>(truncateTextStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ trend }) => {
		if (trend === 'equal') {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			return `${gridSize * 3}px`;
		}

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		return `${gridSize}px`;
	},
});
